import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, X, Pencil, Trash2, FileText, Eye, EyeOff, ChefHat } from "lucide-react";

const STATIONS = [
  { value: "kitchen", label: "Kitchen" },
  { value: "grill", label: "Grill" },
  { value: "cold", label: "Cold section" },
  { value: "bar", label: "Bar" },
  { value: "bakery", label: "Bakery" },
  { value: "other", label: "Other" },
];
const DIETARIES = [
  { value: "", label: "—" },
  { value: "veg", label: "Veg" },
  { value: "non-veg", label: "Non-veg" },
  { value: "vegan", label: "Vegan" },
  { value: "jain", label: "Jain" },
];
const EMPTY = {
  name: "", section: "", station: "kitchen", public_price: 0, cost_price: 0,
  prep_time_min: 0, allergens: "", dietary: "", notes: "", order: 100, active: true,
};

function marginPct(price, cost) {
  const p = Number(price) || 0, c = Number(cost) || 0;
  if (p <= 0) return null;
  return ((p - c) / p) * 100;
}

function marginColor(pct) {
  if (pct === null) return "text-[hsl(var(--ink-soft))]";
  if (pct >= 60) return "text-[hsl(var(--forest))]";
  if (pct >= 40) return "text-[hsl(var(--brass-2))]";
  return "text-[hsl(6_62%_45%)]";
}

export default function StaffMenuInternal() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stationFilter, setStationFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/staff-menu");
      setItems(data);
    } catch { toast.error("Could not load staff menu"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      ...EMPTY,
      ...it,
      allergens: Array.isArray(it.allergens) ? it.allergens.join(", ") : (it.allergens || ""),
    });
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        public_price: Number(form.public_price) || 0,
        cost_price: Number(form.cost_price) || 0,
        prep_time_min: parseInt(form.prep_time_min) || 0,
        order: parseInt(form.order) || 100,
        allergens: form.allergens
          ? form.allergens.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      if (editing === "new") {
        const { data } = await api.post("/staff/staff-menu", payload);
        setItems((p) => [...p, data]);
        toast.success(`Added ${data.name}`);
      } else {
        const { data } = await api.patch(`/staff/staff-menu/${editing.id}`, payload);
        setItems((p) => p.map((x) => (x.id === data.id ? data : x)));
        toast.success(`Updated ${data.name}`);
      }
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}" from the staff menu?`)) return;
    try {
      await api.delete(`/staff/staff-menu/${it.id}`);
      setItems((p) => p.filter((x) => x.id !== it.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const toggleActive = async (it) => {
    try {
      const { data } = await api.patch(`/staff/staff-menu/${it.id}`, { active: !it.active });
      setItems((p) => p.map((x) => (x.id === data.id ? data : x)));
    } catch { toast.error("Update failed"); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/staff/staff-menu/export.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBB-Staff-Menu-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("PDF download failed"); }
    finally { setDownloading(false); }
  };

  const filtered = stationFilter === "all" ? items : items.filter((i) => i.station === stationFilter);
  const grouped = useMemo(() => {
    const g = {};
    for (const it of filtered) {
      const k = it.station || "other";
      (g[k] = g[k] || []).push(it);
    }
    return g;
  }, [filtered]);

  const totals = useMemo(() => {
    const price = items.reduce((s, i) => s + (Number(i.public_price) || 0), 0);
    const cost = items.reduce((s, i) => s + (Number(i.cost_price) || 0), 0);
    return { price, cost, avgMargin: price > 0 ? ((price - cost) / price) * 100 : 0 };
  }, [items]);

  return (
    <div data-testid="staff-menu-internal">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Back office · Staff use only</div>
          <h1 className="mt-2 text-4xl">Staff menu</h1>
          <p className="mt-3 text-sm text-[hsl(var(--ink-soft))] max-w-2xl">
            Internal reference with cost, margin, prep time, allergens and station notes. Not visible on the public site. Download the PDF for kitchen &amp; bar handovers.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="staff-menu-pdf"
            onClick={downloadPdf}
            disabled={downloading || items.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-[hsl(var(--forest))] rounded text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors disabled:opacity-40"
          >
            <FileText size={14} /> {downloading ? "Preparing…" : "Download PDF"}
          </button>
          <button data-testid="staff-menu-new" onClick={openNew} className="btn-brass !py-2.5 !px-5 text-sm">
            <Plus size={14} /> New dish
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      {items.length > 0 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Total items" value={items.length} />
          <Tile label="Menu price sum" value={`₹${Math.round(totals.price).toLocaleString("en-IN")}`} />
          <Tile label="Cost sum" value={`₹${Math.round(totals.cost).toLocaleString("en-IN")}`} />
          <Tile label="Blended margin" value={`${totals.avgMargin.toFixed(0)}%`} accent={totals.avgMargin >= 60 ? "forest" : totals.avgMargin >= 40 ? "brass" : "red"} />
        </div>
      )}

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="inline-flex flex-wrap gap-1 border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] rounded-full p-1">
          {["all", ...STATIONS.map((s) => s.value)].map((s) => (
            <button
              key={s}
              data-testid={`staff-menu-station-${s}`}
              onClick={() => setStationFilter(s)}
              className={`px-3 py-1 text-[11px] uppercase tracking-wider rounded-full transition-all ${
                stationFilter === s ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"
              }`}
            >
              {s === "all" ? "All" : STATIONS.find((x) => x.value === s)?.label || s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 space-y-6">
        {loading && <div className="p-6 text-[hsl(var(--ink-soft))]">Loading…</div>}
        {!loading && items.length === 0 && (
          <div data-testid="staff-menu-empty" className="p-10 text-center text-[hsl(var(--ink-soft))] border border-dashed border-[hsl(var(--rule))]">
            <ChefHat className="mx-auto mb-2 opacity-40" size={28} />
            No staff-menu items yet. Add your first dish above.
          </div>
        )}
        {!loading && Object.entries(grouped).map(([station, list]) => (
          <div key={station}>
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--brass-2))] mb-2">
              {STATIONS.find((x) => x.value === station)?.label || station} <span className="text-[hsl(var(--ink-soft))]">· {list.length}</span>
            </div>
            <div className="border border-[hsl(var(--rule))]">
              <table className="w-full text-sm">
                <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="p-2 text-left">Dish</th>
                    <th className="p-2 text-left">Section</th>
                    <th className="p-2 text-right w-24">Price</th>
                    <th className="p-2 text-right w-24">Cost</th>
                    <th className="p-2 text-right w-24">Margin</th>
                    <th className="p-2 text-right w-20">Prep</th>
                    <th className="p-2 text-left">Allergens / notes</th>
                    <th className="p-2 w-28"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(var(--rule))]">
                  {list.map((it) => {
                    const m = marginPct(it.public_price, it.cost_price);
                    return (
                      <tr key={it.id} data-testid={`staff-menu-row-${it.id}`} className={!it.active ? "opacity-50" : ""}>
                        <td className="p-2">
                          <div className="font-medium flex items-center gap-2">
                            {it.name}
                            {!it.active && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--rule))]">off</span>}
                            {it.dietary && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--brass)/0.15)] text-[hsl(var(--brass-2))]">{it.dietary}</span>}
                          </div>
                        </td>
                        <td className="p-2 text-[hsl(var(--ink-soft))]">{it.section || "—"}</td>
                        <td className="p-2 text-right">{Number(it.public_price) > 0 ? `₹${Number(it.public_price).toLocaleString("en-IN")}` : "—"}</td>
                        <td className="p-2 text-right">{Number(it.cost_price) > 0 ? `₹${Number(it.cost_price).toLocaleString("en-IN")}` : "—"}</td>
                        <td className={`p-2 text-right font-semibold ${marginColor(m)}`}>{m === null ? "—" : `${m.toFixed(0)}%`}</td>
                        <td className="p-2 text-right text-[hsl(var(--ink-soft))]">{it.prep_time_min ? `${it.prep_time_min}m` : "—"}</td>
                        <td className="p-2 text-xs text-[hsl(var(--ink-soft))]">
                          {Array.isArray(it.allergens) && it.allergens.length > 0 && <span><b className="text-[hsl(6_62%_45%)]">Allergens:</b> {it.allergens.join(", ")}<br /></span>}
                          {it.notes}
                        </td>
                        <td className="p-2">
                          <div className="flex justify-end gap-1">
                            <button data-testid={`staff-menu-toggle-${it.id}`} onClick={() => toggleActive(it)} title={it.active ? "Turn off" : "Turn on"} className="p-1.5 rounded border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg-2))]">
                              {it.active ? <Eye size={12} /> : <EyeOff size={12} />}
                            </button>
                            <button data-testid={`staff-menu-edit-${it.id}`} onClick={() => openEdit(it)} className="p-1.5 rounded border border-[hsl(var(--rule))] hover:bg-[hsl(var(--bg-2))]">
                              <Pencil size={12} />
                            </button>
                            <button data-testid={`staff-menu-del-${it.id}`} onClick={() => remove(it)} className="p-1.5 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)] hover:bg-[hsl(6_62%_45%)] hover:text-[hsl(var(--bg))]">
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div data-testid="staff-menu-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
          <form onSubmit={save} className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-2xl p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow">{editing === "new" ? "New staff-menu dish" : "Edit dish"}</div>
                <h2 className="mt-1 text-2xl">{form.name || "—"}</h2>
                {form.public_price > 0 && (
                  <div className={`mt-1 text-xs font-semibold ${marginColor(marginPct(form.public_price, form.cost_price))}`}>
                    Margin: {marginPct(form.public_price, form.cost_price)?.toFixed(0) || "—"}%
                  </div>
                )}
              </div>
              <button type="button" onClick={close} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
            </div>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Dish name *
                <input required data-testid="staff-menu-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="tbb-inp mt-1 w-full" autoFocus />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Section
                <input data-testid="staff-menu-form-section" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} placeholder="e.g. Mains, Cocktails, Snacks" className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Station
                <select data-testid="staff-menu-form-station" value={form.station} onChange={(e) => setForm({ ...form, station: e.target.value })} className="tbb-inp mt-1 w-full">
                  {STATIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Public price (₹)
                <input type="number" min="0" step="0.01" data-testid="staff-menu-form-price" value={form.public_price} onChange={(e) => setForm({ ...form, public_price: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Cost price (₹)
                <input type="number" min="0" step="0.01" data-testid="staff-menu-form-cost" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Prep time (min)
                <input type="number" min="0" data-testid="staff-menu-form-prep" value={form.prep_time_min} onChange={(e) => setForm({ ...form, prep_time_min: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Dietary
                <select data-testid="staff-menu-form-dietary" value={form.dietary} onChange={(e) => setForm({ ...form, dietary: e.target.value })} className="tbb-inp mt-1 w-full">
                  {DIETARIES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Allergens (comma-separated)
                <input data-testid="staff-menu-form-allergens" value={form.allergens} onChange={(e) => setForm({ ...form, allergens: e.target.value })} placeholder="e.g. dairy, nuts, gluten" className="tbb-inp mt-1 w-full" />
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Prep notes / recipe cues
                <textarea rows={3} data-testid="staff-menu-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Simmer gravy 5 min before service. Plate with mint garnish." className="tbb-inp mt-1 w-full resize-none" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Order
                <input type="number" data-testid="staff-menu-form-order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="flex items-center gap-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] mt-4">
                <input type="checkbox" data-testid="staff-menu-form-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Active on the staff menu
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={close} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
              <button type="submit" data-testid="staff-menu-save" disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">{saving ? "Saving…" : "Save dish"}</button>
            </div>
            <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, accent = "ink" }) {
  const accentColor = {
    ink: "text-[hsl(var(--ink))]",
    forest: "text-[hsl(var(--forest))]",
    brass: "text-[hsl(var(--brass-2))]",
    red: "text-[hsl(6_62%_45%)]",
  }[accent] || "text-[hsl(var(--ink))]";
  return (
    <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{label}</div>
      <div className={`mt-1 text-xl font-semibold ${accentColor}`}>{value}</div>
    </div>
  );
}
